import type { ClientMsg, ServerMsg } from '@ic/engine';
import { auth } from './auth';

type Listener = (m: ServerMsg) => void;
const listeners = new Set<Listener>();
let ws: WebSocket | null = null;
let queue: ClientMsg[] = [];
let retry = 0;
let wanted = false;
/** Games this tab follows; joined again (without taking a seat) after a reconnect. */
const joined = new Set<string>();

/** Stable id so a guest keeps their seat after a reconnect. */
function guestId(): string {
  try {
    let id = localStorage.getItem('ic.guestId');
    if (!id) { id = crypto.randomUUID(); localStorage.setItem('ic.guestId', id); }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export type ConnStatus = 'offline' | 'connecting' | 'online';
let status: ConnStatus = 'offline';
const statusSubs = new Set<() => void>();
function setStatus(s: ConnStatus) {
  status = s;
  statusSubs.forEach((f) => f());
}
export const getStatus = () => status;
export const subscribeStatus = (f: () => void) => { statusSubs.add(f); return () => { statusSubs.delete(f); }; };

/** A message from the admins: a banner on top of whatever page (or game) is open. */
function showNotice(text: string) {
  const el = document.createElement('div');
  el.setAttribute('role', 'alert');
  el.textContent = text;
  el.style.cssText = 'position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:100000;max-width:min(560px,92vw);padding:12px 18px;border-radius:12px;background:#1d4f91;color:#fff;font:600 15px system-ui,sans-serif;box-shadow:0 8px 30px rgba(0,0,0,.45);cursor:pointer;text-align:center';
  el.onclick = () => el.remove();
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 15000);
}

function connect() {
  if (ws && ws.readyState <= 1) return;
  setStatus('connecting');
  const url = (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws';
  const sock = new WebSocket(url);
  ws = sock;
  sock.onopen = () => {
    retry = 0;
    setStatus('online');
    sock.send(JSON.stringify({ t: 'hello', token: auth.get().token, guestId: guestId() } satisfies ClientMsg));
    const queued = new Set(queue.flatMap((m) => (m.t === 'join' ? [m.gameId] : [])));
    for (const id of joined) if (!queued.has(id)) sock.send(JSON.stringify({ t: 'join', gameId: id, sit: false } satisfies ClientMsg));
    for (const m of queue) sock.send(JSON.stringify(m));
    queue = [];
  };
  sock.onmessage = (e) => {
    const msg = JSON.parse(e.data) as ServerMsg;
    // The saved login is no longer valid on the server: say so instead of silently playing as a guest.
    if (msg.t === 'welcome' && msg.user.guest && auth.get().token) {
      auth.set({ token: null, user: null, admin: false, hasRecovery: false });
      listeners.forEach((l) => l({ t: 'error', message: 'Your login has expired. Please log in again.' }));
    }
    if (msg.t === 'notice') showNotice(msg.message);
    listeners.forEach((l) => l(msg));
  };
  sock.onclose = () => {
    if (ws === sock) ws = null;
    setStatus('offline');
    if (wanted) setTimeout(connect, Math.min(10000, 500 * 2 ** retry++));
  };
}

export function send(m: ClientMsg) {
  wanted = true;
  if (m.t === 'join') joined.add(m.gameId);
  if (m.t === 'leave') joined.delete(m.gameId);
  if (ws?.readyState === 1) ws.send(JSON.stringify(m));
  else { queue.push(m); connect(); }
}

export function onMessage(l: Listener): () => void {
  wanted = true;
  connect();
  listeners.add(l);
  return () => { listeners.delete(l); };
}

// Reconnect so the server sees a new login.
auth.subscribe(() => { ws?.close(); });
